/**
 * Retry com backoff exponencial para chamadas GraphQL do fluxo financeiro.
 *
 * Complementa (não substitui) o RetryLink do apolloClient: o link já refaz falhas de
 * transporte imediatas (3 tentativas, ~300ms). Esta camada cobre indisponibilidade mais
 * longa — deploy/restart do volanpg, 500 transitório do proxy — dormindo entre tentativas
 * (1s/3s/8s por padrão) antes de desistir de vez.
 *
 * Repete SOMENTE falha de transporte (`error.networkError` do ApolloError: fetch falhou,
 * ERR_FAILED/CORS, 5xx sem corpo GraphQL). Erro de GraphQL (`graphQLErrors`) é
 * determinístico — validação/regra de negócio — e é relançado imediatamente.
 */

export const DEFAULT_RETRY_DELAYS_MS = [1000, 3000, 8000];

// 4xx transitórios: repetir faz sentido apesar da faixa.
//  - 408: é o statusCode do TimeoutError do apollo-link-timeout (default do construtor; o
//    timeoutLink do apolloClient é criado só com o tempo, então cai nesse default). É o caso
//    MAIS importante daqui — chunk do saveTransactions que estoura o `context.timeout` precisa
//    das tentativas com backoff, que é justamente o caminho de recuperação do fechamento grande;
//  - 429: rate limit, cede ao backoff.
const RETRYABLE_4XX = [408, 429];

/**
 * Só falha de transporte é repetível. `networkError` também é populado para ServerError com
 * status 4xx determinístico (token expirado, payload inválido, rota errada) — repetir esses
 * custa as 3 tentativas + ~12s de espera sem chance de sucesso, então são relançados de
 * imediato. Sem `statusCode` (fetch falhou, ERR_FAILED/CORS) segue repetível, assim como 5xx e
 * os 4xx transitórios do RETRYABLE_4XX.
 *
 * @param {*} error
 * @returns {Boolean}
 */
export function isRetryableGqlError(error) {
  if (!error?.networkError) {
    return false;
  }
  const statusCode = error.networkError.statusCode;
  return !statusCode || statusCode >= 500 || RETRYABLE_4XX.includes(statusCode);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Executa `fn` repetindo falhas de transporte com backoff + jitter (50% do delay).
 * A operação repetida precisa ser idempotente (queries sempre; mutations só se upsert).
 *
 * @param {String} label - identificação nos logs de retry
 * @param {function(): Promise<*>} fn - a chamada (query/mutate) a executar
 * @param {{delays?: Number[], isRetryable?: function(*): Boolean}=} options
 * @returns {Promise<*>}
 */
export async function gqlWithRetry(label, fn, {delays = DEFAULT_RETRY_DELAYS_MS, isRetryable = isRetryableGqlError} = {}) {
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    }
    catch (error) {
      if (attempt >= delays.length || !isRetryable(error)) {
        throw error;
      }
      const delay = delays[attempt] + Math.floor(Math.random() * delays[attempt] * 0.5);
      attempt++;
      console.warn(`${label}: falha de transporte no GraphQL, nova tentativa ${attempt}/${delays.length} em ${delay}ms`,
        error?.networkError?.message || error?.message || error);
      await sleep(delay);
    }
  }
}
